/**
 * UserChip — shared user chip with logout dropdown.
 * Used across all three roles; one component, consistent behaviour everywhere.
 *
 * Usage:
 *   import UserChip from "../../components/UserChip.jsx";
 *   <UserChip user={user} onLogout={handleLogout} />
 *
 * Props:
 *   user      — from useAuth(): { display_name, full_name, email, initials, role }
 *   onLogout  — async function; typically: await logout(); navigate("/login")
 */

import React, { useState, useRef, useEffect } from "react";
import { roleLabel } from "../lib/roles.js";

export default function UserChip({ user, onLogout }) {
  const [open, setOpen]     = useState(false);
  const [busy, setBusy]     = useState(false);
  const containerRef        = useRef();

  // Close on outside click
  useEffect(() => {
    function onMouseDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  // Close on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  async function handleLogout() {
    setBusy(true);
    try { await onLogout(); }
    finally { setBusy(false); setOpen(false); }
  }

  // roleLabel() returns "" for an unknown role rather than falling back to the
  // raw value — that fallback is what put "family_owner" on a parent's screen.
  const label = roleLabel(user?.role);

  return (
    <div ref={containerRef} style={s.wrap}>
      <button
        style={s.chip}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div style={s.avatar}>{user?.initials || "?"}</div>
        <span style={s.label}>{user?.display_name}{label && ` · ${label}`}</span>
        <span style={{ ...s.chevron, transform: open ? "rotate(180deg)" : "none" }}>▾</span>
      </button>

      {open && (
        <div style={s.menu} role="menu">
          <div style={s.menuHead}>
            <p style={s.menuName}>{user?.full_name || user?.display_name}</p>
            {user?.email && <p style={s.menuEmail}>{user.email}</p>}
          </div>
          <div style={s.divider} />
          <button
            style={{ ...s.menuItem, ...(busy ? s.menuItemBusy : {}) }}
            role="menuitem"
            onClick={handleLogout}
            disabled={busy}
          >
            {busy ? "Logging out…" : "Log out"}
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:        { position: "relative" },
  chip:        { display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: "var(--radius-full)", transition: "background 0.1s" },
  avatar:      { width: 28, height: 28, borderRadius: "50%", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-semibold)", fontSize: 12, flexShrink: 0 },
  label:       { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", whiteSpace: "nowrap" },
  chevron:     { fontSize: 10, color: "var(--color-text-tertiary)", transition: "transform 0.15s" },
  menu:        { position: "absolute", top: "calc(100% + 6px)", right: 0, background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 200, zIndex: 200, overflow: "hidden" },
  menuHead:    { padding: "12px 16px 10px" },
  menuName:    { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", margin: 0 },
  menuEmail:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0, marginTop: 2 },
  divider:     { height: 1, background: "var(--color-border)" },
  menuItem:    { display: "block", width: "100%", textAlign: "left", padding: "10px 16px", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "none", cursor: "pointer" },
  menuItemBusy:{ opacity: 0.5, cursor: "not-allowed" },
};
